import { cn, formatBytes } from '@/lib/utils'
import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { Link } from '@tanstack/react-router'

import {
  FrozenReason,
  Organisation,
  frozenReasonFor,
} from '@droidmap/organisation-service-contract'
import { StorageSummary } from '@droidmap/shared-service-contract'

export interface OrgRollup {
  projectCount: number
  memberCount: number
}

export interface OrgCardProps {
  org: Organisation
  /** Roll-up of fanout queries (project + member counts). Undefined → loading. */
  rollup?: OrgRollup
}

const totalStorageBytes = (s: StorageSummary | undefined): number => {
  if (!s) return 0
  return (Object.values(s) as unknown[]).reduce<number>(
    (sum, v) => sum + (typeof v === 'number' ? v : 0),
    0,
  )
}

const formatStorage = (bytes: number): string => formatBytes(bytes, 1)

// Six gradient swatches keyed off a hash of the org name so each org gets
// a stable identity color across reloads.
const LOGO_GRADIENTS = [
  'bg-gradient-to-br from-[rgb(var(--accent))] to-[rgb(var(--accent-2))] text-btn-fg',
  'bg-gradient-to-br from-[rgb(var(--violet))] to-[#7a6aff] text-white',
  'bg-gradient-to-br from-[#3bd4c4] to-[rgb(var(--accent-2))] text-[#001418]',
  'bg-gradient-to-br from-[#ff8a5b] to-[#ff5b8a] text-white',
  'bg-gradient-to-br from-[rgb(var(--success))] to-[#3bd4c4] text-[#001a12]',
  'bg-gradient-to-br from-[rgb(var(--warning))] to-[#ff8a5b] text-[#1a0e00]',
] as const

const hashString = (input: string): number => {
  let hash = 0
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Why a frozen org is frozen, as the card's status pill (#2598). A second org
 * is created already frozen and must be findable from the list — this is the
 * one place the user sees *why* before entering it and meeting the takeover.
 */
const FROZEN_PILL: Record<FrozenReason, string> = {
  trial_expired: 'Trial ended',
  awaiting_activation: 'Awaiting activation',
}

function FrozenPill({ org }: { org: Organisation }) {
  // A deleted org inside its restore window (#2973) stays listed so an admin
  // can find it and restore it; the pill says why it won't open normally.
  if (org.lifecycleState === 'pending_delete') {
    return (
      <span className="border-border bg-danger-soft text-text rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider">
        Deleted
      </span>
    )
  }
  const reason = frozenReasonFor(org)
  if (!reason) return null
  return (
    <span className="border-border bg-warning-soft text-text rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider">
      {FROZEN_PILL[reason]}
    </span>
  )
}

function Stat({
  value,
  label,
  divider,
}: {
  value: React.ReactNode
  label: string
  divider?: boolean
}) {
  return (
    <div
      className={cn(
        'relative py-3.5 text-center',
        divider &&
          "before:bg-border before:absolute before:bottom-[20%] before:left-0 before:top-[20%] before:w-px before:content-['']",
      )}
    >
      <div className="text-text font-mono text-[18px] font-medium leading-none tracking-tight">
        {value}
      </div>
      <div className="text-micro text-text-mute mt-1.5">{label}</div>
    </div>
  )
}

/**
 * Meridian organisation card. Renders a hashed-color gradient logo,
 * name, ULID, three-stat strip (Members / Projects / Storage), and a
 * footer with asset count + "Enter →". Pass `rollup` once the per-org
 * fanout query settles; until then the card shows `··` placeholders.
 */
export function OrgCard({ org, rollup }: OrgCardProps) {
  const gradient = LOGO_GRADIENTS[hashString(org.name) % LOGO_GRADIENTS.length]
  const storage = totalStorageBytes(org.storageSummary)
  const pending = rollup === undefined
  const pendingValue = (
    <span className="text-text-mute" aria-label="Loading">
      ··
    </span>
  )

  return (
    <Link
      to="/orgs/$orgId"
      params={{ orgId: org.id }}
      className={cn(
        'group bg-panel border-border relative flex flex-col overflow-hidden rounded-lg border',
        'transition-[transform,border-color,box-shadow] duration-b ease-meridian',
        'hover:border-accent-tint hover:shadow-elevated hover:-translate-y-0.5',
        'focus-visible:border-accent-tint focus-visible:ring-accent-soft focus-visible:outline-hidden focus-visible:ring-2',
      )}
    >
      <div
        aria-hidden
        className="grid-overlay duration-b ease-meridian pointer-events-none absolute inset-0 opacity-50 transition-opacity group-hover:opacity-100"
      />
      <div className="relative z-[1] flex items-start justify-between gap-3 px-5 pb-4 pt-5">
        <div
          className={cn(
            'grid h-12 w-12 place-items-center rounded-md font-mono text-base font-bold tracking-tight shadow-md',
            gradient,
          )}
        >
          {initials(org.name)}
        </div>
        <FrozenPill org={org} />
      </div>
      <div className="relative z-[1] flex-1 px-5 pb-4">
        <h3 className="text-text mb-1 text-[17px] font-semibold leading-tight tracking-tight">
          {org.name}
        </h3>
        <div className="text-text-mute mb-4 truncate font-mono text-[10px] tracking-wide">
          {org.id}
        </div>
        <div className="border-border -mx-5 grid grid-cols-3 border-y">
          <Stat
            value={pending ? pendingValue : (rollup?.memberCount ?? 0)}
            label={rollup?.memberCount === 1 ? 'Member' : 'Members'}
          />
          <Stat
            value={pending ? pendingValue : (rollup?.projectCount ?? 0)}
            label="Projects"
            divider
          />
          <Stat value={formatStorage(storage)} label="Storage" divider />
        </div>
      </div>
      <div className="border-border bg-bg-2 relative z-[1] flex items-center justify-between border-t px-5 py-3">
        <span className="text-text-dim font-mono text-[11px] uppercase tracking-wider">
          {org.assetCount ?? 0} {org.assetCount === 1 ? 'asset' : 'assets'}
        </span>
        <span className="text-text-mute group-hover:text-accent inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider transition-colors">
          Enter
          <ArrowRightIcon className="duration-f ease-meridian h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  )
}

export default OrgCard
